/**
 * The IndexedDB layer: large binaries (bake atlases, models, textures) kept in the browser by path
 * and content hash, within a size cap (the player's setting, cache.setting.ts), the least recently
 * used dropped first, and within the browser's quota.
 *
 * Every call fails soft: with IndexedDB missing or blocked (private windows in some browsers), the
 * quota full or the database broken, a read finds nothing and a write keeps nothing, so the game
 * goes to the network as it did before. Nothing here throws.
 *
 * Two object stores: `meta` (path, hash, size, last use: read whole when the database opens, so
 * the cap is kept without touching the bytes) and `blobs` (the bytes, by path).
 *
 * Safe to import from workers: it touches nothing until called.
 */
import { type CachedMeta, evictionPlan, stalePaths } from './lru';

const DB_NAME = 'nine-swords.assets';
const DB_VERSION = 1;
const META = 'meta';
const BLOBS = 'blobs';
/** The cap until the setting says otherwise (pages without the settings, such as the studio). */
const DEFAULT_CAP = 1e9;
/** Opening the database never holds up a load longer than this. */
const OPEN_MS = 3000;
/** Last-use times are written together, this long after a read. */
const TOUCH_MS = 2000;

let capOf = (): number => DEFAULT_CAP;
/** Where the cap comes from (bytes; 0 keeps nothing): the player's setting. */
export function setCacheCap(f: () => number): void {
  capOf = f;
}

function ask<T>(r: IDBRequest<T>): Promise<T> {
  return new Promise((ok, fail) => {
    r.onsuccess = () => ok(r.result);
    r.onerror = () => fail(r.error ?? new Error('IndexedDB request failed'));
  });
}

function finished(tx: IDBTransaction): Promise<void> {
  return new Promise((ok, fail) => {
    tx.oncomplete = () => ok();
    tx.onerror = () => fail(tx.error ?? new Error('IndexedDB transaction failed'));
    tx.onabort = () => fail(tx.error ?? new Error('IndexedDB transaction aborted'));
  });
}

let warned = false;
function warn(what: string, error: unknown): void {
  if (warned) return;
  warned = true;
  // A warning, not an error: the game carries on from the network.
  console.warn(`Asset cache: ${what}, loading from the network instead`, error);
}

/**
 * Paths of files made in this browser rather than downloaded (figure frames, render/rig/frameCache.ts):
 * no manifest lists them, so `prune` leaves them to their maker.
 */
export const MADE_HERE = 'frames/';

/** Room to leave free in the browser's quota for everything else the game stores. */
const quotaMargin = (quota: number): number => Math.max(20e6, quota * 0.1);

class AssetStore {
  private db: Promise<IDBDatabase | null> | null = null;
  private index: Map<string, CachedMeta> | null = null;
  private touched = new Set<string>();
  private touchTimer: ReturnType<typeof setTimeout> | undefined;
  /** Writes in order, one at a time, so eviction sees the sizes of those before. */
  private writing: Promise<unknown> = Promise.resolve();

  /** The database, opened once (null when it cannot be). */
  open(): Promise<IDBDatabase | null> {
    this.db ??= (async () => {
      try {
        if (typeof indexedDB === 'undefined' || !indexedDB) return null;
        const db = await new Promise<IDBDatabase>((ok, fail) => {
          const timer = setTimeout(() => fail(new Error('IndexedDB did not open in time')), OPEN_MS);
          const r = indexedDB.open(DB_NAME, DB_VERSION);
          r.onupgradeneeded = () => {
            const d = r.result;
            if (!d.objectStoreNames.contains(META)) d.createObjectStore(META, { keyPath: 'path' });
            if (!d.objectStoreNames.contains(BLOBS)) d.createObjectStore(BLOBS);
          };
          r.onsuccess = () => {
            clearTimeout(timer);
            ok(r.result);
          };
          r.onerror = () => {
            clearTimeout(timer);
            fail(r.error ?? new Error('IndexedDB did not open'));
          };
          r.onblocked = () => {
            clearTimeout(timer);
            fail(new Error('IndexedDB is blocked by another tab'));
          };
        });
        // Another tab upgrading or deleting the database: let go of it.
        db.onversionchange = () => {
          db.close();
          this.db = null;
          this.index = null;
        };
        const all = await ask(db.transaction(META).objectStore(META).getAll() as IDBRequest<CachedMeta[]>);
        this.index = new Map(all.map((m) => [m.path, m]));
        return db;
      } catch (error) {
        warn('IndexedDB is unavailable', error);
        return null;
      }
    })();
    return this.db;
  }

  /** Bytes kept (null until the database has opened). */
  usage(): number | null {
    if (!this.index) return null;
    let n = 0;
    for (const m of this.index.values()) n += m.size;
    return n;
  }

  /** The files kept, without their bytes. */
  entries(): CachedMeta[] {
    return [...(this.index?.values() ?? [])];
  }

  /** A file's bytes, if kept with this hash. */
  async read(path: string, hash: string): Promise<Blob | null> {
    try {
      const db = await this.open();
      const m = this.index?.get(path);
      if (!db || !m || m.hash !== hash) return null;
      const blob = (await ask(db.transaction(BLOBS).objectStore(BLOBS).get(path))) as unknown;
      if (!(blob instanceof Blob) || blob.size !== m.size) {
        void this.remove([path]);
        return null;
      }
      this.touch(path);
      return blob;
    } catch (error) {
      warn('a read failed', error);
      return null;
    }
  }

  /** Keeps a file under its hash, dropping the least recently used to make room. True if kept. */
  write(path: string, hash: string, blob: Blob): Promise<boolean> {
    const job = this.writing.then(() => this.put(path, hash, blob));
    this.writing = job.catch(() => undefined);
    return job;
  }

  private async put(path: string, hash: string, blob: Blob): Promise<boolean> {
    try {
      const db = await this.open();
      if (!db || !this.index) return false;
      const size = blob.size;
      let budget = capOf();
      // The browser's quota for this site, less what it already holds and a margin.
      try {
        const est = await globalThis.navigator?.storage?.estimate?.();
        if (est?.quota) {
          const free = est.quota - (est.usage ?? 0) - quotaMargin(est.quota);
          budget = Math.min(budget, (this.usage() ?? 0) + free);
        }
      } catch {
        // No estimate: the cap alone.
      }
      const drop = evictionPlan(this.entries(), budget, size, path);
      if (!drop) return false;
      const meta: CachedMeta = { path, hash, size, used: Date.now() };
      const tx = db.transaction([META, BLOBS], 'readwrite');
      for (const p of drop) {
        tx.objectStore(META).delete(p);
        tx.objectStore(BLOBS).delete(p);
      }
      tx.objectStore(META).put(meta);
      tx.objectStore(BLOBS).put(blob, path);
      await finished(tx);
      for (const p of drop) this.index.delete(p);
      this.index.set(path, meta);
      return true;
    } catch (error) {
      // Most likely the quota: make room for next time by dropping the older half.
      if (error instanceof DOMException && error.name === 'QuotaExceededError') {
        const old = this.entries().sort((a, b) => a.used - b.used);
        await this.remove(old.slice(0, Math.ceil(old.length / 2)).map((m) => m.path));
      } else warn('a write failed', error);
      return false;
    }
  }

  /** Drops these files. */
  async remove(paths: readonly string[]): Promise<void> {
    if (!paths.length) return;
    try {
      const db = await this.open();
      if (!db) return;
      const tx = db.transaction([META, BLOBS], 'readwrite');
      for (const p of paths) {
        tx.objectStore(META).delete(p);
        tx.objectStore(BLOBS).delete(p);
      }
      await finished(tx);
      for (const p of paths) this.index?.delete(p);
    } catch (error) {
      warn('a delete failed', error);
    }
  }

  /** Drops the least recently used until what is kept fits the cap. */
  async shrink(): Promise<void> {
    await this.open();
    const drop = evictionPlan(this.entries(), Math.max(0, capOf()), 0) ?? [];
    await this.remove(drop);
  }

  /** Drops what the manifest no longer lists, or lists with another hash (an older version). */
  async prune(files: Readonly<Record<string, { hash?: unknown } | null | undefined>>): Promise<void> {
    await this.open();
    await this.remove(stalePaths(this.entries().filter((m) => !m.path.startsWith(MADE_HERE)), files));
  }

  /** Drops everything kept. */
  async clear(): Promise<void> {
    try {
      const db = await this.open();
      if (!db) return;
      const tx = db.transaction([META, BLOBS], 'readwrite');
      tx.objectStore(META).clear();
      tx.objectStore(BLOBS).clear();
      await finished(tx);
      this.index?.clear();
    } catch (error) {
      warn('clearing failed', error);
    }
  }

  /** Marks a file used now (written in a batch a moment later). */
  private touch(path: string): void {
    const m = this.index?.get(path);
    if (m) m.used = Date.now();
    this.touched.add(path);
    this.touchTimer ??= setTimeout(() => {
      this.touchTimer = undefined;
      const paths = [...this.touched];
      this.touched.clear();
      void (async () => {
        try {
          const db = await this.open();
          if (!db || !this.index) return;
          const tx = db.transaction(META, 'readwrite');
          for (const p of paths) {
            const meta = this.index.get(p);
            if (meta) tx.objectStore(META).put(meta);
          }
          await finished(tx);
        } catch {
          // Only the order of eviction suffers.
        }
      })();
    }, TOUCH_MS);
  }
}

/** The one store of the page. */
export const assetStore = new AssetStore();
